import React, {useCallback, useReducer, useState} from 'react';
import {FolderOpenIcon} from '@heroicons/react/24/outline';
import {MeshType} from 'api/generated';
import {Button, Modal} from 'components';
import {useARStore} from 'stores';
import {classNames} from 'utils';
import {NoImg} from 'assets';
import ARButtonModelSelect from './AR.Button.Model.Select';

interface IARButtonFolder {}

const ARButtonFolder = ({}: IARButtonFolder) => {
  const [show, toggleShow] = useReducer(s => !s, false);
  const setModelData = useARStore(s => s.setModelData);
  const setModelActive = useARStore(s => s.setModelActive);
  const isModelInteractiveRef = useARStore(s => s.isModelInteractiveRef);
  const noModels = useARStore(s => s.models.length === 0);
  const modelData = useARStore(s => s.modelData);

  const handleOpen = () => {
    isModelInteractiveRef.current = !isModelInteractiveRef.current;
    toggleShow();
  };

  const handleClose = useCallback(() => {
    isModelInteractiveRef.current = !isModelInteractiveRef.current;
    toggleShow();
  }, [toggleShow]);

  const handleConfirm = useCallback(
    (mesh: MeshType) => {
      setModelData({data: mesh});
      setModelActive({id: '', justPlaced: false});
    },
    [setModelData],
  );

  return (
    <>
      <div className="absolute bottom-4 right-4 flex flex-col gap-1">
        <button
          type="button"
          onClick={handleOpen}
          className={classNames(
            'flex justify-center items-center bg-white rounded-full size-16 transition duration-300 active:scale-95',
            noModels && !modelData && !show && 'animate-bounce',
          )}>
          <FolderOpenIcon className="size-8 text-slate-600" />
        </button>
        <p className="text-sm text-center text-white drop-shadow-[0_0_1px_black] select-none">MODELS</p>
      </div>
      <ARButtonFolderModal isVisible={show} onConfirm={handleConfirm} onClose={handleClose} />
    </>
  );
};

export default ARButtonFolder;

interface IARButtonFolderModalProps {
  isVisible: boolean;
  onConfirm: (selected: MeshType) => void;
  onClose: () => void;
}

const ARButtonFolderModal = React.memo(({isVisible, onConfirm, onClose}: IARButtonFolderModalProps) => {
  const [selected, setSelected] = useState<MeshType>();
  const meshes = useARStore(s => s.meshes);

  const handleConfirm = () => {
    selected && onConfirm(selected);
    handleClose();
  };

  const handleClose = () => {
    onClose();
    setSelected(undefined);
  };

  return (
    <Modal isVisible={isVisible} handlePressOutside={handleClose} usePortal={false}>
      <h2 className="text-2xl mb-1.5">Select model</h2>
      <ol className="list-disc ml-4 text-slate-600 text-lg select-none">
        <li>You may load multiple models</li>
        <li>Place model one by one (if multiple)</li>
      </ol>
      <div className="max-h-80 grid grid-cols-2 gap-3 overflow-y-scroll no-scrollbar my-6">
        {meshes.map(mesh => {
          return (
            <ARButtonModelSelect
              key={mesh.uid}
              img={mesh.imageUrl || NoImg}
              label={mesh.name}
              onClick={() => setSelected(mesh)}
              selected={selected?.uid === mesh.uid}
            />
          );
        })}
      </div>
      <Button text="Confirm" fullWidth onClick={handleConfirm} disabled={!selected} />
    </Modal>
  );
});
